import {checkIfNeutralPlayer} from "../players/checkIfNeutralPlayer";

// Отряды одного вида — одной иконкой со счетчиком. Вид — то, от чего зависит
// иконка (см. getUnitIconDisplayProps): тип, владелец, фракция и исходный
// владелец соблазненного отряда
const getUnitGroupKey = ({type, ownerId, factionId, capturedFromOwnerId}) => (
  [type, ownerId, factionId ?? '', capturedFromOwnerId ?? ''].join('|')
);

const addUnitToGroups = (groups, unit) => {
  const key = getUnitGroupKey(unit);
  if (!groups.has(key)) {
    groups.set(key, {
      type: unit.type,
      ownerId: unit.ownerId,
      factionId: unit.factionId ?? null,
      capturedFromOwnerId: unit.capturedFromOwnerId ?? null,
      count: 0,
    });
  }
  groups.get(key).count += 1;
};

const addToMap = (map, playerId, value) => {
  map[playerId] = (map[playerId] || 0) + (Number(value) || 0);
};

/**
 * Сводка этапа боя по игрокам — для модалки итогов боя: какие отряды игрок
 * уничтожил и какие потерял, сколько очков получил, сколько урона нанес и
 * сколько размера своих отрядов потерял из общего размера всех своих отрядов
 * до этапа. Считается редьюсером этапа (ближний бой, стрельба, ловушки) и
 * лежит в gameState.combatStats рядом с combatResults.
 *
 * В сводку попадают только игроки, у которых что-то изменилось. Нейтральный
 * игрок — не соперник: его строки нет, а его потерянные отряды видны в
 * строках уничтоживших.
 *
 * Без отрядов до и после этапа размер не считается (sizeLost и sizeBefore —
 * null), без урона по игрокам — урон (damageDealt — null): так собирается
 * запасная сводка из одних уничтожений для состояний без combatStats.
 *
 * @param {Object} params
 * @param {Object[]} params.results - уничтожения этапа (combatResults)
 * @param {Object[]} params.players - игроки партии
 * @param {Object[]} [params.unitsBefore] - отряды до этапа
 * @param {Object[]} [params.unitsAfter] - отряды после этапа (без погибших)
 * @param {Object} [params.damageDealtByPlayer] - нанесенный урон: {playerId: число}
 * @param {Object} [params.bonusWinPointsByPlayer] - очки этапа сверх очков за
 *   уничтожение (Варварство): {playerId: число}
 * @returns {{playerId: string, killedUnits: Object[], lostUnits: Object[],
 *   winPoints: number, damageDealt: number|null, sizeLost: number|null,
 *   sizeBefore: number|null}[]} в порядке игроков партии; killedUnits и
 *   lostUnits — [{type, ownerId, factionId, capturedFromOwnerId, count}]
 */
export const buildCombatStats = ({
  results, players, unitsBefore, unitsAfter, damageDealtByPlayer, bonusWinPointsByPlayer,
}) => {
  const hasSizes = Array.isArray(unitsBefore) && Array.isArray(unitsAfter);
  const killedById = {};
  const lostById = {};
  const winPointsById = {...bonusWinPointsByPlayer};
  const sizeBeforeById = {};
  const sizeLostById = {};

  (results || []).forEach(({killedUnit, killerPlayerId, winPoints}) => {
    if (!killedUnit) {
      return;
    }
    if (killerPlayerId && !checkIfNeutralPlayer(killerPlayerId)) {
      killedById[killerPlayerId] = killedById[killerPlayerId] || new Map();
      addUnitToGroups(killedById[killerPlayerId], killedUnit);
      addToMap(winPointsById, killerPlayerId, winPoints);
    }
    if (killedUnit.ownerId && !checkIfNeutralPlayer(killedUnit.ownerId)) {
      lostById[killedUnit.ownerId] = lostById[killedUnit.ownerId] || new Map();
      addUnitToGroups(lostById[killedUnit.ownerId], killedUnit);
    }
  });

  if (hasSizes) {
    const sizeAfterByUnitId = new Map(unitsAfter.map((unit) => [unit.id, Math.max(0, Number(unit.size) || 0)]));
    unitsBefore.forEach((unit) => {
      const sizeBefore = Math.max(0, Number(unit.size) || 0);
      addToMap(sizeBeforeById, unit.ownerId, sizeBefore);
      addToMap(sizeLostById, unit.ownerId, Math.max(0, sizeBefore - (sizeAfterByUnitId.get(unit.id) ?? 0)));
    });
  }

  return (players || [])
    .filter((player) => !checkIfNeutralPlayer(player.id))
    .map((player) => ({
      playerId: player.id,
      killedUnits: [...(killedById[player.id]?.values() || [])],
      lostUnits: [...(lostById[player.id]?.values() || [])],
      winPoints: winPointsById[player.id] || 0,
      damageDealt: damageDealtByPlayer ? (damageDealtByPlayer[player.id] || 0) : null,
      sizeLost: hasSizes ? (sizeLostById[player.id] || 0) : null,
      sizeBefore: hasSizes ? (sizeBeforeById[player.id] || 0) : null,
    }))
    .filter((stats) => stats.killedUnits.length > 0 || stats.lostUnits.length > 0
      || stats.winPoints !== 0 || stats.damageDealt > 0 || stats.sizeLost > 0);
};

/**
 * Сводка этапа для модалки: состояния без сводки (запись до нее) считаются из
 * самих уничтожений
 * @param {Object[]|null|undefined} combatStats - gameState.combatStats
 * @param {Object[]} combatResults - gameState.combatResults
 * @param {Object[]} players - игроки партии
 * @returns {Object[]}
 */
export const resolveCombatStats = (combatStats, combatResults, players) => (
  Array.isArray(combatStats) && combatStats.length > 0
    ? combatStats
    : buildCombatStats({results: combatResults, players})
);

const mergeUnitGroups = (earlier, later) => {
  const groups = new Map();
  [...earlier, ...later].forEach((group) => {
    const key = getUnitGroupKey(group);
    groups.set(key, {...group, count: (groups.get(key)?.count || 0) + group.count});
  });
  return [...groups.values()];
};

const mergeCombatStatsRows = (earlier, later) => {
  const isSizeKnown = typeof earlier.sizeLost === 'number' && typeof later.sizeLost === 'number';
  const isDamageKnown = earlier.damageDealt !== null || later.damageDealt !== null;
  return {
    ...earlier,
    killedUnits: mergeUnitGroups(earlier.killedUnits, later.killedUnits),
    lostUnits: mergeUnitGroups(earlier.lostUnits, later.lostUnits),
    winPoints: earlier.winPoints + later.winPoints,
    damageDealt: isDamageKnown ? (earlier.damageDealt || 0) + (later.damageDealt || 0) : null,
    sizeLost: isSizeKnown ? earlier.sizeLost + later.sizeLost : null,
    // Между этапами отрядов могло прибавиться (итоги не закрыты до хода другого
    // игрока): потери не должны превысить то, что было в игре
    sizeBefore: isSizeKnown ? Math.max(earlier.sizeBefore, earlier.sizeLost + later.sizeBefore) : null,
  };
};

/**
 * Общая сводка нескольких этапов: стрельбы и ближнего боя одного хода или
 * этапов, итоги которых игрок еще не закрыл.
 * @param {Object[]} earlier - сводка более раннего этапа
 * @param {Object[]} later - сводка более позднего этапа
 * @returns {Object[]}
 */
export const mergeCombatStats = (earlier, later) => {
  const laterByPlayerId = new Map(later.map((row) => [row.playerId, row]));
  const earlierPlayerIds = new Set(earlier.map((row) => row.playerId));
  return [
    ...earlier.map((row) => (
      laterByPlayerId.has(row.playerId) ? mergeCombatStatsRows(row, laterByPlayerId.get(row.playerId)) : row
    )),
    ...later.filter((row) => !earlierPlayerIds.has(row.playerId)),
  ];
};

/**
 * Урон ближнего боя по игрокам: сумма распределенного по противникам урона
 * его отрядов (damageDistribution — без урона сверх нужного на уничтожение)
 * @param {Object[]} pendingMeleeDamage
 * @returns {Object} {playerId: число}
 */
export const sumMeleeDamageByPlayer = (pendingMeleeDamage) => {
  const damageByPlayer = {};
  (pendingMeleeDamage || []).forEach(({ownerId, damageDistribution}) => {
    Object.values(damageDistribution || {}).forEach((damage) => addToMap(damageByPlayer, ownerId, damage));
  });
  return damageByPlayer;
};

/**
 * Сводка этапа перемещения или маневра, на котором сработали ловушки Гоблинов.
 * Отряды до этапа восстанавливаются по срабатываниям этапа
 * (goblinsTrapStageTriggers хранят снимок отряда до урона), урон — по
 * владельцам сработавших ловушек. Состояние без срабатываний (старые записи)
 * — сводка из одних уничтожений
 * @param {Object[]} killResults - уничтожения ловушками за этап
 * @param {Object[]|undefined} stageTriggers - gameState.goblinsTrapStageTriggers
 * @param {Object[]} units - отряды после этапа
 * @param {Object[]} players - игроки партии
 */
export const buildTrapStageCombatStats = (killResults, stageTriggers, units, players) => {
  if (!Array.isArray(stageTriggers)) {
    return buildCombatStats({results: killResults, players});
  }

  const unitBeforeById = new Map();
  stageTriggers.forEach(({unit}) => {
    if (unit && !unitBeforeById.has(unit.id)) {
      unitBeforeById.set(unit.id, unit);
    }
  });
  const unitsBefore = [
    ...units.map((unit) => (unitBeforeById.has(unit.id) ? {...unit, size: unitBeforeById.get(unit.id).size} : unit)),
    ...[...unitBeforeById.values()].filter((unit) => !units.some(({id}) => id === unit.id)),
  ];

  const damageDealtByPlayer = {};
  stageTriggers.forEach(({triggeredTrapEvents}) => (triggeredTrapEvents || []).forEach(({trapsByOwner}) => (
    (trapsByOwner || []).forEach(({ownerId, damage}) => addToMap(damageDealtByPlayer, ownerId, damage))
  )));

  return buildCombatStats({results: killResults, players, unitsBefore, unitsAfter: units, damageDealtByPlayer});
};
